import { Clock, DollarSign, AlertTriangle, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

const PAINS = [
  { icon: Clock, title: "Waiting for updates", desc: "Days or weeks of back-and-forth for a change you needed live yesterday." },
  { icon: DollarSign, title: "Paying for simple changes", desc: "Invoices for a price change or a new photo add up fast." },
  { icon: AlertTriangle, title: "Outdated information", desc: "Old hours and stale pricing quietly cost you real customers." },
  { icon: Lock, title: "Developer dependency", desc: "Every routine update means chasing down someone else to do it." },
];

const SOLUTIONS = [
  "Manage your website yourself in seconds — no tickets, no waiting",
  "No per-change fees for everyday updates",
  "Your information stays accurate and always current",
  "Complete ownership and control of your content",
];

export const Problem = () => {
  return (
    <section className="relative bg-background py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="mb-4 inline-block rounded-full bg-accent px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
            The Problem
          </span>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
            You Don&rsquo;t Need Another Website. You Need One You Can Manage.
          </h2>
          <p className="mt-5 text-base text-muted-foreground md:text-lg">
            Most business owners already have a website — the problem is they can&rsquo;t touch it.
            Every small change means waiting on a developer. Sound familiar?
          </p>
        </Reveal>

        <StaggerGroup className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PAINS.map((p) => (
            <StaggerItem key={p.title}>
              <div className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-destructive/30 hover:shadow-card">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-destructive/10 text-destructive">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold text-navy">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        {/* Solution panel */}
        <Reveal dir="up" delay={0.1} className="mt-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-navy p-8 shadow-elegant lg:p-12">
            <div className="absolute inset-0 grid-pattern opacity-30" />
            <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-techblue/20 blur-3xl" />
            <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
              <div>
                <span className="mb-4 inline-block rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-techblue-glow">
                  The Anchor Solution
                </span>
                <h3 className="font-display text-2xl font-bold leading-snug text-navy-foreground sm:text-3xl">
                  A website management platform built for business owners.
                </h3>
                <p className="mt-4 text-navy-muted">
                  We build your professional website, then give you the Anchor platform to manage
                  it yourself. Update anything, anytime — no code, no waiting, no developer required.
                </p>
                <Button asChild variant="cta" size="lg" className="mt-7">
                  <a href="#how-it-works">
                    See how it works <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
              <ul className="space-y-3">
                {SOLUTIONS.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-navy-foreground backdrop-blur"
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-techblue-glow" />
                    <span className="text-sm font-medium">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
